import { Anchor, Stack } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Missing } from "@tfg/ui"
import _ from "lodash"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { BoolCell } from "~/components/cells/BoolCell.tsx"
import { CodeCell } from "~/components/cells/CodeCell.tsx"
import { DateCell } from "~/components/cells/DateCell.tsx"
import { LastPendingIrContactCell } from "~/components/cells/LastPendingIrContactCell.tsx"
import { OperationCountriesCell } from "~/components/cells/OperationCountriesCell.tsx"
import { StandardCell } from "~/components/cells/StandardCell.tsx"
import { SupplierCell } from "~/components/cells/SupplierCell.tsx"
import { isCertificateVerified } from "~/pages/Brand/Products/List/Services/standardCompliance.tsx"

const columnHelper = createColumnHelper<LibraryResponses.Product>()

const operationRankAccessor =
  (rank: number) =>
  (row: LibraryResponses.Product): LibraryResponses.OperationNode[] => {
    if (rank > row.maxRank) return []
    return row.operationTree.nodes.filter((node) => node.rank === rank)
  }

export const useColumns = (
  maxRank: number,
  showOnlyMainFabric: boolean,
  customFieldKeys: string[],
  standardHeaders: LibraryResponses.Product["standards"][number][],
  onStandardClick: (standard: LibraryResponses.ProductStandard) => void
) => {
  const navigate = useNavigate()
  const { t } = useTranslation(["products"])

  return useMemo<ColumnDef<LibraryResponses.Product, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        cell: (info) => (
          <Anchor
            fz="sm"
            c="gray.9"
            onClick={() =>
              navigate(`/products/details/${info.row.original.id}`)
            }
          >
            {info.getValue()}
          </Anchor>
        ),
      }),
      columnHelper.accessor("brand", {
        id: "brand",
        header: t("lists.columns.brand"),
        enableSorting: false,
        cell: (info) => info.getValue() ?? <Missing c="gray" />,
      }),
      columnHelper.accessor("reference", {
        id: "reference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        cell: (info) => <CodeCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrder.reference", {
        id: "purchaseOrder",
        header: t("lists.columns.purchaseOrder"),
        enableSorting: false,
        cell: (info) =>
          info.row.original.purchaseOrder?.reference ? (
            <CodeCell value={info.row.original.purchaseOrder.reference} />
          ) : (
            <Missing c="gray" />
          ),
      }),
      columnHelper.accessor("color", {
        header: t("lists.columns.color"),
        enableSorting: false,
      }),
      columnHelper.accessor("brandType", {
        id: "type",
        header: t("lists.columns.type"),
        enableSorting: false,
      }),
      columnHelper.accessor("collection", {
        header: t("lists.columns.collection"),
        enableSorting: false,
        cell: (info) => info.getValue() ?? <Missing c="gray" />,
      }),
      columnHelper.accessor("ingestedAt", {
        id: "ingestedAt",
        header: t("lists.columns.ingestedAt"),
        enableSorting: false,
        cell: (info) => <DateCell date={info.getValue()} format="DD/MM/YYYY" />,
      }),
      ..._.times(maxRank + 1, (i) =>
        columnHelper.accessor(operationRankAccessor(i), {
          id: `rank-${i}`,
          header: t("lists.columns.rank", { rank: i + 1 }),
          enableSorting: false,
          cell: (info) => {
            return (
              <Stack h="100%" py={4}>
                <OperationCountriesCell
                  operationNodes={info.getValue()}
                  showOnlyMainFabric={showOnlyMainFabric}
                />
              </Stack>
            )
          },
        })
      ),
      columnHelper.accessor("recycledMaterial", {
        header: t("lists.columns.recycledMaterialPercentage"),
        enableSorting: false,
        cell: (info) => info.getValue().percentage,
      }),
      columnHelper.accessor("releaseMicroplastics", {
        header: t("lists.columns.releaseMicroplastics"),
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue()
          return val !== "missing-main-component" ? (
            <BoolCell value={val} />
          ) : (
            <BoolCell value={undefined} />
          )
        },
      }),
      columnHelper.accessor("directSupplier", {
        id: "supplier",
        header: t("lists.columns.directSupplier"),
        enableSorting: false,
        cell: (info) => {
          const value = info.row.original.directSupplier
          return <SupplierCell directSupplier={value} />
        },
      }),
      columnHelper.accessor("lastPendingIrContact", {
        id: "lastPendingIrContact",
        header: t("lists.columns.pendingIrContact"),
        enableSorting: false,
        cell: (info) => {
          const lastPendingIrContact = info.getValue()
          return <LastPendingIrContactCell supplier={lastPendingIrContact} />
        },
      }),
      ...customFieldKeys.map((customFieldKey) =>
        columnHelper.accessor((p) => p.flattenCustomFields[customFieldKey], {
          id: "cfd-" + customFieldKey,
          header: customFieldKey,
          enableSorting: false,
          cell: (info) =>
            info.getValue() === "" ? <Missing c="gray" /> : info.getValue(),
        })
      ),
      ...standardHeaders.map((standardHeader) =>
        columnHelper.accessor(
          (p) => {
            const standard = p.standards?.find(
              (standard) => standard.name == standardHeader.name
            )
            const totalCertificates = standard?.certificates.length
            const completedCertificates = standard?.certificates.filter(
              (certificate) => isCertificateVerified(certificate)
            ).length

            return p.standards && totalCertificates
              ? `${completedCertificates} / ${totalCertificates}`
              : ""
          },
          {
            id: standardHeader.name,
            header: standardHeader.name,
            enableSorting: false,
            cell: (info) => {
              const value = info.row.original.standards?.find(
                (standard) => standard.name == standardHeader.name
              )
              return <StandardCell value={value} onClick={onStandardClick} />
            },
          }
        )
      ),
      columnHelper.accessor("id", {
        id: "id",
        header: undefined,
        enableSorting: false,
        cell: (info) => (
          <ActionIcon
            type="external-link"
            onClick={() => navigate(`/products/details/${info.getValue()}`)}
          />
        ),
      }),
    ],
    [
      t,
      maxRank,
      customFieldKeys,
      standardHeaders,
      navigate,
      showOnlyMainFabric,
      onStandardClick,
    ]
  )
}
